import React from 'react';
import { Avatar, Card, Col, List, Row, Space, Statistic, Tag, Typography, Spin, Tooltip as AntTooltip } from 'antd';
import { RiseOutlined, DollarOutlined, ShoppingCartOutlined, FireOutlined, WalletOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useGetDashboardKPIs } from '../../../hooks/useInformes';

const { Title, Text } = Typography;

const SeccionDashboard = ({ desde, hasta }) => {
    const { data: kpis, isLoading, isError } = useGetDashboardKPIs({ desde, hasta });

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px', color: '#64748b', fontWeight: '500' }}>Calculando finanzas...</div>
            </div>
        );
    }

    if (isError || !kpis) {
        return <div style={{ textAlign: 'center', marginTop: '50px' }}><Text type="danger">Error al cargar los datos del dashboard.</Text></div>;
    }

    return (
        <>
            <Row gutter={[12, 12]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={4}>
                    <Card bordered={false} styles={{ body: { padding: '16px' } }} style={{ borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <Statistic
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Ventas Brutas</Text>}
                            value={kpis.ventasTotales} prefix={<DollarOutlined style={{ color: '#22BABB' }} />}
                            formatter={val => `$${val.toLocaleString('es-CO')}`} valueStyle={{ color: '#0f172a', fontWeight: '800', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card bordered={false} styles={{ body: { padding: '16px' } }} style={{ borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <Statistic
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Devoluciones</Text>}
                            value={kpis.devoluciones} prefix={<RiseOutlined rotate={180} style={{ color: '#ef4444' }} />}
                            formatter={val => `-$${val.toLocaleString('es-CO')}`} valueStyle={{ color: '#ef4444', fontWeight: '800', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card bordered={false} styles={{ body: { padding: '16px' } }} style={{ borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <Statistic
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Costo Mercancía</Text>}
                            value={kpis.costosMercancia} prefix={<ShoppingCartOutlined style={{ color: '#f59e0b' }} />}
                            formatter={val => `$${val.toLocaleString('es-CO')}`} valueStyle={{ color: '#475569', fontWeight: '700', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card bordered={false} styles={{ body: { padding: '16px' } }} style={{ borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <Statistic
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Gastos Operativos</Text>}
                            value={kpis.gastosOperativos} prefix={<WalletOutlined style={{ color: '#ef4444' }} />}
                            formatter={val => `$${val.toLocaleString('es-CO')}`} valueStyle={{ color: '#475569', fontWeight: '700', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={24} lg={8}>
                    <Card bordered={false} styles={{ body: { padding: '16px' } }} style={{ borderRadius: '12px', background: 'linear-gradient(135deg, #22BABB 0%, #0d9488 100%)' }}>
                        <Statistic
                            title={
                                <Space>
                                    <Text style={{ color: '#ccfbf1', fontSize: '14px', fontWeight: '600' }}>Utilidad Neta Real</Text>
                                    <AntTooltip title="Dinero libre restando Costos, Gastos y Devoluciones a clientes." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#ccfbf1', cursor: 'help' }} />
                                    </AntTooltip>
                                </Space>
                            }
                            value={kpis.utilidadNeta} prefix={<RiseOutlined style={{ color: '#fff' }} />}
                            formatter={val => `$${val.toLocaleString('es-CO')}`} valueStyle={{ color: '#fff', fontWeight: '900', fontSize: '28px' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                {/* GRÁFICO RECHARTS */}
                <Col xs={24} xl={16}>
                    <Card
                        title={
                            <Space>
                                <Title level={4} style={{ margin: 0, color: '#1e293b' }}>Evolución de Ingresos y Gastos</Title>
                                {/* TOOLTIP 2: Evolución */}
                                <AntTooltip title="Compara visualmente el dinero que entra (ventas) frente al que sale (gastos) día a día en el rango de fechas seleccionado." color="#0f172a">
                                    <InfoCircleOutlined style={{ color: '#94a3b8', cursor: 'help', fontSize: '16px' }} />
                                </AntTooltip>
                            </Space>
                        }
                        bordered={false}
                        style={{ borderRadius: '16px', height: '100%', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}
                        styles={{ body: { height: '350px', padding: '24px 24px 0 0' } }}
                    >
                        <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                            <AreaChart data={kpis.evolucion} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#22BABB" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#22BABB" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorGastos" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="dia" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(value) => `$${value / 1000}k`} />
                                <Tooltip formatter={(value) => `$${value.toLocaleString('es-CO')}`} />
                                <Area type="monotone" dataKey="ingresos" stroke="#22BABB" strokeWidth={3} fillOpacity={1} fill="url(#colorIngresos)" name="Ingresos" />
                                <Area type="monotone" dataKey="gastos" stroke="#ef4444" strokeWidth={3} fillOpacity={1} fill="url(#colorGastos)" name="Gastos" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                {/* TOP 5 PRODUCTOS REAL */}
                <Col xs={24} xl={8}>
                    <Card
                        title={
                            <Space>
                                <FireOutlined style={{ color: '#ef4444' }} />
                                <Title level={4} style={{ margin: 0, color: '#1e293b' }}>Top 5 Productos</Title>
                                {/* TOOLTIP 3: Top Productos */}
                                <AntTooltip title="Muestra los 5 productos que más dinero le han generado al negocio, ordenados por los ingresos totales." color="#0f172a">
                                    <InfoCircleOutlined style={{ color: '#94a3b8', cursor: 'help', fontSize: '16px' }} />
                                </AntTooltip>
                            </Space>
                        }
                        bordered={false}
                        style={{ borderRadius: '16px', height: '100%', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}
                        styles={{ body: { padding: '0 24px 24px 24px' } }}
                    >
                        {kpis.topProductos?.length === 0 ? (
                            <div style={{ textAlign: 'center', marginTop: '50px', color: '#94a3b8' }}>No hay ventas en este periodo</div>
                        ) : (
                            <List
                                itemLayout="horizontal"
                                dataSource={kpis.topProductos}
                                renderItem={(item, index) => (
                                    <List.Item style={{ padding: '16px 0', borderBottom: index === 4 ? 'none' : '1px solid #f1f5f9' }}>
                                        <List.Item.Meta
                                            avatar={
                                                <Avatar style={{ backgroundColor: index === 0 ? '#fef08a' : index === 1 ? '#e2e8f0' : index === 2 ? '#ffedd5' : '#f8fafc', color: index === 0 ? '#ca8a04' : index === 1 ? '#475569' : index === 2 ? '#c2410c' : '#94a3b8', fontWeight: 'bold' }}>
                                                    {index + 1}
                                                </Avatar>
                                            }
                                            title={<Text strong style={{ color: '#1e293b', fontSize: '14px' }}>{item.nombre}</Text>}
                                            description={<Text type="secondary">{item.ventas} unids.</Text>}
                                        />
                                        <div style={{ textAlign: 'right' }}>
                                            <Text strong style={{ color: '#0f172a', display: 'block' }}>${item.ingresos.toLocaleString('es-CO')}</Text>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </>
    )
}

export default SeccionDashboard;